import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState } from "react";
import ResizeObserver from "resize-observer-polyfill";
import { Tooltip } from "../tooltip/Tooltip";
import { useCallbackRef } from "../hooks/useCallbackRef";
import { useCombineRefs } from "../hooks/useCombineRefs";
import * as Styled from "./MultilineEllipsis.styled";
const defaultStringMap = {
    ellipsisLabel: "…"
};
const defaultTooltipProps = {
    placement: "top-end"
};
export const MultilineEllipsis = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { children = null, stringMap = defaultStringMap, lines = 2, minWidth, maxWidth = 290, disableWordBreak, tooltipLocation = "ellipsis", tooltipProps = defaultTooltipProps, onOverflowChange, ...otherProps } = props;
    const outerRef = useRef(null);
    const ellipsisRef = useRef(null);
    const isOverflowingRef = useRef(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    // Store this value on a ref so we can use it in an effect without having to teardown and re-run every time it changes
    const onOverflowChangeRef = useCallbackRef(onOverflowChange);
    useEffect(() => {
        const isSSR = typeof window === "undefined" || typeof document === "undefined";
        const wrapper = outerRef.current;
        const ellipsisWrapper = ellipsisRef.current;
        if (!ellipsisWrapper || isSSR || !wrapper) {
            return;
        }
        const handleResize = (_entries) => {
            // TODO use entries to detect if the ellipsisWrapper has changed size instead of checking offsetHeight every time
            if (ellipsisWrapper.scrollHeight > 0 && ellipsisWrapper.offsetHeight > 0) {
                const isOverflowing = ellipsisWrapper.scrollHeight - 1 > ellipsisWrapper.offsetHeight;
                const onOverflowChange = onOverflowChangeRef.current;
                const overflowHasChanged = isOverflowingRef.current !== isOverflowing;
                // TODO only update state if the overflow has changed and debounce the callback
                setIsOverflowing(isOverflowing);
                if (onOverflowChange && overflowHasChanged) {
                    onOverflowChange(isOverflowing);
                    isOverflowingRef.current = isOverflowing;
                }
            }
        };
        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(wrapper);
        return () => {
            resizeObserver.disconnect();
        };
    }, [ellipsisRef, lines, children, onOverflowChangeRef]);
    const multilineEllipsis = (_jsx(Styled.MultilineEllipsis, { ...otherProps, ref: useCombineRefs(outerRef, ref), "data-testid": "MultilineEllipsis", hasTextTooltip: Boolean(isOverflowing && tooltipLocation === "text"), children: _jsxs(Styled.EllipsisContainer, { lines: lines, minWidth: typeof minWidth === "number" ? `${minWidth}px` : minWidth, maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth, disableWordBreak: disableWordBreak, ref: ellipsisRef, children: [children, isOverflowing && tooltipLocation === "ellipsis" && (_jsx(Tooltip, { ...tooltipProps, isSilent: true, trigger: _jsx(Styled.EllipsisTooltipTrigger, { children: _jsx("span", { children: stringMap.ellipsisLabel }) }), contents: children }))] }) }));
    if (isOverflowing && tooltipLocation === "text") {
        return _jsx(Tooltip, { ...tooltipProps, trigger: multilineEllipsis, contents: children });
    }
    return multilineEllipsis;
});
MultilineEllipsis.displayName = "MultilineEllipsis";
MultilineEllipsis.defaultProps = {
    lines: 2,
    maxWidth: 290,
    tooltipLocation: "ellipsis",
    tooltipProps: defaultTooltipProps,
    stringMap: defaultStringMap
};
